import React, { useState, useEffect } from "react";
import { FileOpenOutlined } from "@mui/icons-material";
import { message, Space } from "antd";
import bootstrap from "bootstrap/dist/js/bootstrap.bundle.min";
import { formatDateTime } from "Common/DateTimeFormat";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import { useLocation } from "react-router-dom";

function useInterviewHooks() {
  const location = useLocation();
  const applicantIdFromState =
    location.state?.jobApplicantId || location.state?.job?.jobApplicantId;
  const applicantId = applicantIdFromState || localStorage.getItem("userId");
  const [jobApplicantId, setJobApplicantId] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState([]);
  const [loader, setLoader] = useState(true);
  const [employeeInterview, setEmployeeInterview] = useState(null);
  const [employeeContract, setEmployeeContract] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [selectedPdf, setSelectedPdf] = useState("");
  const [showNavbar, setShowNavbar] = useState(true);
  const [submittedEmployees, setSubmittedEmployees] = useState({});
  const [isTimeSubmitted, setIsTimeSubmitted] = useState(false);
  const [isContractSubmitted, setIsContractSubmitted] = useState(false);
  const apiUrl = process.env.REACT_APP_Base_URL;
  const [selectedTime, setSelectedTime] = useState("");
  const [employeeData, setEmployeeData] = useState(null);
  const [isAllFilesSelected, setIsAllFilesSelected] = useState(false);

  const requiredFileTypes = [
    "ndafile",
    "ncafile",
    "employeeAgreement",
    "i_nine",
    "w_four",
    "offerLetter",
    "hrPolicy",
  ];

  useEffect(() => {
    const allSelected = requiredFileTypes.every(
      (type) => uploadedFiles[type] && uploadedFiles[type] instanceof File
    );
    setIsAllFilesSelected(allSelected);
  }, [uploadedFiles]);

  useEffect(() => {
    if (!jobApplicantId || !employeeData?.status) return;

    // NOTE: the old single-interview endpoint (Get-By-JobApplicantId) was
    // replaced by Mudassir's multi-round /applicant/rounds API. The new
    // ApplicantInterviewRounds component fetches directly from that endpoint,
    // so we no longer need fetchEmployeeData() here — calling it just produces
    // a 404 in the Network tab. Kept the function defined below in case other
    // legacy callers need it.

    // Contract exists only AFTER document is sent
    if (employeeData.status >= 6) {
      fetchContractByEmail();
    }
  }, [jobApplicantId, employeeData?.status]);

  const openContractSection = () => {
    if (employeeData?.status < 6) return;
    fetchContractByEmail();
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoader(true);
      const response = await axiosInstance.get(API.hiringJobApplicant.getById(applicantId));
      const responseData = response.data;
        setEmployeeData(responseData);

      if (Array.isArray(responseData)) {
        setEmployeeDetails(responseData);
        if (responseData[0]?.jobApplicantId) {
          setJobApplicantId(responseData[0].jobApplicantId);
        }
      } else {
        setEmployeeDetails([responseData]);
        if (responseData?.jobApplicantId) {
          setJobApplicantId(responseData.jobApplicantId);
        }
          }
    } catch (error) {
      console.error("Error fetching employee data", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (employeeInterview?.firstDateTime) {
      setSelectedTime(employeeInterview.firstDateTime);
    }
  }, [employeeInterview]);

  async function fetchEmployeeData() {
    try {
      if (jobApplicantId) {
        const response = await axiosInstance.get(
          API.hiringInterviews.getByJobApplicantId(jobApplicantId)
        );
        const responseData = response.data;


        if (Array.isArray(responseData) && responseData.length > 0) {
          setEmployeeInterview(responseData[0]);
        } else {
          setEmployeeInterview(responseData);
        }
      }
    } catch (error) {
      // This is the OLD single-interview endpoint. Mudassir replaced it with
      // the multi-round /applicant/rounds API used by ApplicantInterviewRounds.
      // 404s here are expected for new-flow applicants — don't spam the console.
      if (error.response?.status !== 404) {
        console.error("Error fetching legacy employee interview data:", error);
      }
    }
  }

  async function employeeAction(jobApplicantId) {
    try {
      const emailData = {
        jobApplicantId: jobApplicantId,
      };

      await axiosInstance.post(API.hiringJobApplicant.addJobApplicantAction, emailData);
      message.success("Your response send to the organization thanks");
    } catch (error) {
      console.error("Error sending email:", error);
    }
  }

  async function contractAcceptance() {
    try {
      const formData = new FormData();
      if (!employeeContract?.id || employeeData?.status < 6) return;

      const contractId = employeeContract.id;
         formData.append("ContractId", contractId);

      // Append each file from uploadedFiles state
      requiredFileTypes.forEach((fileType) => {
        if (uploadedFiles[fileType]) {
          formData.append(
            fileType.charAt(0).toUpperCase() + fileType.slice(1),
            uploadedFiles[fileType]
          );
        }
      });
  
      await axiosInstance.post(API.hiringContracts.contractAcceptance, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      message.success("Your contract response sended");
      setIsContractSubmitted(true);
    } catch (error) {
      message.error("Please select all files");
    }
  }

  const handleShortlistClick = async (employee) => {
    try {
  
      await employeeAction(employee.jobApplicantId);

      // Mark this employee as submitted only if API call succeeds
      setSubmittedEmployees((prev) => ({ ...prev, [employee.jobApplicantId]: true }));
    } catch (error) {
      console.error("Error in handleShortlistClick:", error);
      message.error("Failed to send your response. Please try again.");
    }
  };

  async function fetchContractByEmail() {
    try {
      if (jobApplicantId) {
        const response = await axiosInstance.get(
          API.hiringContracts.getContractByApplicant(jobApplicantId)
        );
        const responseData = response.data;
        const modifiedResponseData = {
          ...responseData,
          contractId: responseData.jobApplicantId,
        };
        // setEmployeeContract(responseData);
        setEmployeeContract(modifiedResponseData);
      } else {
        console.error("User email not found in local storage");
      }
    } catch (error) {
      console.error("Error fetching contract data:", error);
    }
  }

  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedDateTime, setSelectedDateTime] = useState(null);

  // Function to handle when the user selects a time
  const handleTimeSelect = (jobApplicantId, event) => {
    setSelectedEmployeeId(jobApplicantId);
    setSelectedDateTime(event.target.value);
    setSelectedTime(event.target.value);
  };

  const handleSelectedTime = async () => {
    try {
      if (!selectedDateTime) {
        message.error("Please select a time.");
        return;
      }

      let selectedSlot = 0;
      if (selectedDateTime === employeeInterview.firstDateTime) {
        selectedSlot = 1;
      } else if (selectedDateTime === employeeInterview.secondDateTime) {
        selectedSlot = 2;
      } else if (selectedDateTime === employeeInterview.thirdDateTime) {
        selectedSlot = 3;
      }

      await axiosInstance.post(API.hiringInterviews.selectSchedule, {
        jobInterviewId: employeeInterview.id, // Use jobApplicantId as interview ID
        selectedSlot: selectedSlot,
      });

      message.success("Your Interview Schedule is sent to the Organization. Thanks!");
      setIsTimeSubmitted(true);
      localStorage.setItem("istimeSubmitted", true);
    } catch (error) {
      console.error("Error handling interview:", error);
      if (error.response?.data) {
        console.error("DEBUG - Error response:", error.response.data);
        message.error(`Error: ${JSON.stringify(error.response.data)}`);
      } else {
        message.error("An error occurred while scheduling the interview.");
      }
    }
  };

  const handlePdfPreview = async (fileUrl) => {
    try {
      // Get the file extension
      const extension = fileUrl.split(".").pop().toLowerCase();

      if (extension === "pdf") {
        // If PDF, set the selected PDF state and open modal for preview
        setSelectedPdf(fileUrl);
        setShowNavbar(false);
        const modal = new bootstrap.Modal(document.getElementById("staticBackdrop"));
        modal.show();
      } else {
        // If not PDF, download the file
        window.open(fileUrl, "_blank");
      }
    } catch (error) {
      console.error("Error handling file:", error);
    }
  };

  const handleContractChange = (e, fileType) => {
    const file = e.target.files[0];
    const allowedExtensions = [".pdf", ".PDF", ".docx", ".DOCX", ".doc", ".DOC"];

    if (file) {
      const fileExtension = file.name.split(".").pop();
      if (!allowedExtensions.includes("." + fileExtension)) {
        alert("Unsupported file type. Only PDF and DOCX files are allowed.");
        return;
      }

      // Replace the setEmployeeContract part with:
      setUploadedFiles((prev) => {
        const updatedFiles = {
          ...prev,
          [fileType]: file,
        };
        return updatedFiles;
      });
    }
  };

  const columns = [
    {
      title: "File Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Download/Preview",
      key: "download",
      render: (record) => (
        <Space size="middle">
          <FileOpenOutlined
            onClick={() => handlePdfPreview(`${apiUrl}/${record.file}`)}
            style={{ cursor: "pointer" }}
          />
        </Space>
      ),
    },
    {
      title: "Upload",
      key: "upload",
      render: (record) => (
        <Space size="middle">
          <input
            type="file"
            onChange={(e) => handleContractChange(e, record.key)}
            accept=".pdf,.PDF,.docx,.DOCX,.doc,.DOC"
          />
        </Space>
      ),
    },
  ];

  return {
    loader,
    employeeContract,
    employeeDetails,
    columns,
    selectedTime,
    employeeData,
    isAllFilesSelected,
    handleSelectedTime,
    handleTimeSelect,
    formatDateTime,
    handleShortlistClick,
    contractAcceptance,
    selectedPdf,
    showNavbar,
    submittedEmployees,
    isContractSubmitted,
    isTimeSubmitted,
    employeeInterview,
    setSelectedTime,
    setShowNavbar,
  };
}
export default useInterviewHooks;
